import { type IntlShape } from 'dibs-react-intl';
import { localStorage } from 'dibs-browser-storage';
import { RECENT_SEARCHES } from './sharedSearchBarConstants.ts';
import { defineMessages } from 'dibs-react-intl';
import { type Suggestions } from '../hooks/useSearchSuggestions.ts';

const titles = defineMessages({
    RECENT: {
        id: 'search.suggestions.recent.type',
        defaultMessage: 'Recent searches',
    },
    POPULAR: {
        id: 'search.suggestions.popular.type',
        defaultMessage: 'Popular searches',
    },
    CREATOR: {
        id: 'search.suggestions.creator.type',
        defaultMessage: 'Creators',
    },
    STYLE: {
        id: 'search.suggestions.style.type',
        defaultMessage: 'Styles',
    },
    SELLER: {
        id: 'search.suggestions.seller.type',
        defaultMessage: 'Sellers',
    },
    CATEGORY: {
        id: 'search.suggestions.category.type',
        defaultMessage: 'Categories',
    },
});

type SectionsByType = {
    title: string;
    urlSection: string | null;
};
const sectionsByType = (intl: IntlShape): Record<string, SectionsByType> => ({
    RECENT: {
        title: intl.formatMessage(titles.RECENT),
        urlSection: null,
    },
    POPULAR: {
        title: intl.formatMessage(titles.POPULAR),
        urlSection: null,
    },
    CREATOR: {
        title: intl.formatMessage(titles.CREATOR),
        urlSection: 'creators',
    },
    STYLE: {
        title: intl.formatMessage(titles.STYLE),
        urlSection: 'styles',
    },
    SELLER: {
        title: intl.formatMessage(titles.SELLER),
        urlSection: 'dealers',
    },
    CATEGORY: {
        title: intl.formatMessage(titles.CATEGORY),
        urlSection: null,
    },
});

export type RecentSearch = {
    type: string;
    terms: { correctedTerm?: string; term: string; resultCount?: number; urlLabel: string }[];
};

const getSectionPropsByType = (
    type: string | null | undefined,
    intl: IntlShape
): SectionsByType => {
    return sectionsByType(intl)?.[type || ''] || {};
};

// analytics needs full `term` value for dealer storefront click through
const createSearchLink = ({
    term,
    urlSection,
    urlLabel,
    type,
}: {
    term: string;
    urlSection?: string | null;
    urlLabel?: string | null;
    type?: string | null;
}): string => {
    const convertedValue = encodeURIComponent(term.toLowerCase());
    let href;

    if (urlSection && urlLabel) {
        href = `/${urlSection}/${urlLabel}/?search=${convertedValue}`;
    } else if (!urlSection && urlLabel && type !== RECENT_SEARCHES.TYPE) {
        // urlLabel is stored as full link in RECENT object
        // Only use urlLabel to build link if not from RECENT
        href = `${urlLabel}?search=${convertedValue}`;
    } else {
        href = `/search/?q=${convertedValue}`;
    }

    return href;
};

const dedupeLocalStorage = (): RecentSearch[] | undefined => {
    const storageData = localStorage.getItem<RecentSearch[]>(RECENT_SEARCHES.LOCAL_STORAGE) || [];
    const dedupeData: RecentSearch[] = [];

    storageData.forEach((section, index) => {
        const terms: Record<string, true> = {};
        dedupeData.push({ type: section.type, terms: [] });
        section.terms.forEach(termObj => {
            if (!terms[termObj.term]) {
                terms[termObj.term] = true;
                dedupeData[index].terms.push(termObj);
            }
        });
        if (section.type === RECENT_SEARCHES.TYPE) {
            dedupeData[index].terms = dedupeData[index].terms.slice(0, RECENT_SEARCHES.MAX_TERMS);
        }
    });
    return dedupeData.length ? dedupeData : undefined;
};

export type CursorTerm = {
    term: string;
    highlightId: string;
    link: string;
};

const getCursorTerms = (data: Suggestions, currentValue: string, intl: IntlShape): CursorTerm[] => {
    const termsArray = (data || []).map(suggestion => {
        const { terms, type } = suggestion || {};
        return (terms || []).map((item, index) => {
            const { term, urlLabel } = item || {};
            const termValue = term || '';
            const { urlSection } = getSectionPropsByType(type, intl);
            return {
                term: termValue,
                highlightId: `${termValue}-${type}-${index}`,
                link: createSearchLink({ term: termValue, urlSection, urlLabel, type }),
            };
        });
    });
    const terms: CursorTerm[] = termsArray.flat();
    terms.push({ term: currentValue, highlightId: `${currentValue}-USER_INPUT`, link: '' });
    return terms;
};

const handleRecentStorage = (term: string, urlLabel: string): void => {
    const search = { term, urlLabel };
    let localRecentSearches =
        localStorage.getItem<RecentSearch[]>(RECENT_SEARCHES.LOCAL_STORAGE) || [];
    let recentTerms = [];

    if (!localRecentSearches.length) {
        localRecentSearches = [
            {
                type: RECENT_SEARCHES.TYPE,
                terms: [search],
            },
        ];
    } else {
        recentTerms = localRecentSearches[0].terms;
        if (recentTerms.length === RECENT_SEARCHES.MAX_STORED_TERMS) {
            recentTerms.pop();
        }
        recentTerms.unshift(search);
    }

    localStorage.setItem(RECENT_SEARCHES.LOCAL_STORAGE, JSON.stringify(localRecentSearches));
};

const cleanUpRecentSearches = (): void => {
    const storageData = localStorage.getItem<RecentSearch[]>(RECENT_SEARCHES.LOCAL_STORAGE) || [];
    const cleanedRecentSearches = storageData.reduce<RecentSearch[]>(
        (cleanedStorageData, section) => {
            if (section.type && Array.isArray(section.terms)) {
                section.terms = section.terms.filter(({ term, urlLabel }) => term && urlLabel);
                return [...cleanedStorageData, section];
            }
            return cleanedStorageData;
        },
        []
    );
    localStorage.setItem(RECENT_SEARCHES.LOCAL_STORAGE, JSON.stringify(cleanedRecentSearches));
};

const updateRecentSearchResultEntry = ({
    resultCount,
    originalSearchTerm,
    correctedTerm,
}: {
    resultCount: number;
    originalSearchTerm?: string | null;
    correctedTerm?: string | null;
}): void => {
    if (!resultCount || !originalSearchTerm) {
        return;
    }

    const [storageData] = localStorage.getItem<RecentSearch[]>(RECENT_SEARCHES.LOCAL_STORAGE) || [];

    if (!storageData || !storageData.terms) {
        return;
    }

    const termToUpdate = originalSearchTerm;
    const entry = storageData.terms?.find(
        t => t.term && t.term.toLowerCase() === termToUpdate.toLowerCase()
    );

    if (!entry) {
        return;
    }

    entry.resultCount = resultCount;

    if (correctedTerm && correctedTerm !== entry.term) {
        entry.correctedTerm = correctedTerm;
    }

    localStorage.setItem(RECENT_SEARCHES.LOCAL_STORAGE, JSON.stringify([storageData]));
};

const noop = (): void => undefined;

export {
    getSectionPropsByType,
    createSearchLink,
    dedupeLocalStorage,
    getCursorTerms,
    handleRecentStorage,
    cleanUpRecentSearches,
    updateRecentSearchResultEntry,
    noop,
};
